import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next/';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';

import {
    BodyTextSmall,
    BodyTextMedium,
    HeadingText40,
    HeadingText60,
    pxToRem,
    CalloutInline,
} from '@modernhealth/vital-components';

import { layout } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';

import { navigationService } from 'src/config/rootService';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { CareNavigationRequestSource, DeviceCategory } from 'src/generated/webapp_gql';
import OurTeam from 'src/public/img/our_team.png';
import { CareNavigationRequest } from 'src/modules/careNavigation/models/careNavigation';
import { InternalLink } from 'src/shared/components/InternalLink';
import { useIsCareNavEnabledUser } from 'src/modules/careNavigation/hooks/useIsCareNavEnabledUser';

import { BenefitCardSlider } from '../components/BenefitCardSlider';
import { CategoryItem } from '../components/BenefitContentCard';
import { getFeaturedBenefitsAndCategories } from '../selectors/benefitsSelectors';
import { AllYourBenefitsPageTestId } from '../models/testsIds';

const Container = styled.div({
    paddingTop: '48px',
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

const BenefitsContainer = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: '-24px',
    paddingBottom: '26px',
});

const Benefit = styled.div({
    padding: '12px',
    paddingRight: 0,
    paddingLeft: '24px',
    width: '576px',
});

const CareNavigationBannerContainer = styled.div({
    maxWidth: pxToRem(400),
});

const CalloutInlineContainer = styled.div({
    display: 'flex',
    gap: pxToRem(16),
});

const CalloutInlineImage = styled.img({
    borderRadius: '50%',
    height: 48,
    width: 48,
});

export const AllYourBenefitsPage: React.FC = () => {
    const user = useSelector(getUserInformation);
    const { benefitCategories, featuredBenefits } = useSelector(getFeaturedBenefitsAndCategories);
    const shouldShowBenefitBanner = benefitCategories.length > 0 || featuredBenefits.length > 0;
    const { t } = useTranslation();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();
    const analytics = useAnalytics();

    const handleCategorySelection = topic => {
        analytics.trackClick('Individual Benefit Category Selection', {
            button_copy: topic.name,
        });

        navigationService.navigate('/benefit-per-category', {
            topicOfFocus: topic,
        });
    };

    useEffect(() => {
        if (!user.companySegment && user.companySegments?.length !== 0) {
            navigationService.navigate('/choose-your-segment');
        }
    }, [user.companySegment, user.companySegments]);

    useEffect(() => {
        if (!shouldShowBenefitBanner) {
            navigationService.navigate('/');
        }
    }, [shouldShowBenefitBanner]);

    const requestState: CareNavigationRequest = {
        source: CareNavigationRequestSource.BenefitsPage,
        topic: '',
        platform: DeviceCategory.Web,
        message: '',
        contactPreference: '',
    };

    return (
        <Container>
            <HeadingText60 marginBottom={16} testID={AllYourBenefitsPageTestId.Title}>
                {t('benefits:all_your_benefits_page.title', { company: user.company })}
            </HeadingText60>
            <BodyTextMedium marginBottom={64} testID={AllYourBenefitsPageTestId.Subtitle}>
                {t('benefits:all_your_benefits_page.subtitle')}
            </BodyTextMedium>
            {featuredBenefits.length > 0 && (
                <>
                    <HeadingText40 testID={AllYourBenefitsPageTestId.FeaturedBenefitsSubtitle}>
                        {t('benefits:all_your_benefits_page.featured_benefits')}
                    </HeadingText40>

                    <BenefitCardSlider featuredBenefits={featuredBenefits} />
                </>
            )}

            <HeadingText40
                marginTop={32}
                testID={AllYourBenefitsPageTestId.ExploreYourBenefitsSubtitle}
            >
                {t('benefits:all_your_benefits_page.explore_your_benefits')}
            </HeadingText40>
            <BenefitsContainer data-testid={AllYourBenefitsPageTestId.TopicsList}>
                {benefitCategories.map(topic => {
                    //TODO change this hardcoded name in BE to match the translation json variable so we can do something like t(`benefits:${topic.name}`), Mobile code must change as well
                    if (topic.name === 'all_your_benefits_page.other_benefit') {
                        topic.name = t('benefits:all_your_benefits_page.other_benefits');
                    }
                    return (
                        topic && (
                            <Benefit key={topic.name}>
                                <CategoryItem
                                    element={topic}
                                    onClick={() => handleCategorySelection(topic)}
                                />
                            </Benefit>
                        )
                    );
                })}
            </BenefitsContainer>

            {isUserEnabledFlow && (
                <CareNavigationBannerContainer
                    data-testid={AllYourBenefitsPageTestId.CareNavigationBanner}
                >
                    <CalloutInline variant="info" showIcon={false}>
                        <CalloutInlineContainer>
                            <CalloutInlineImage
                                alt="{t('careNavigation:benefits_page_care_options.title')}"
                                src={OurTeam}
                            />
                            <div>
                                <BodyTextSmall marginBottom={4}>
                                    {t('careNavigation:benefits_page_care_options.title')}
                                </BodyTextSmall>
                                <InternalLink
                                    to={{
                                        pathname: '/care-navigation-request',
                                        state: requestState,
                                    }}
                                >
                                    {t('careNavigation:benefits_page_care_options.description')}
                                </InternalLink>
                            </div>
                        </CalloutInlineContainer>
                    </CalloutInline>
                </CareNavigationBannerContainer>
            )}
        </Container>
    );
};
